import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InputGroup, NumericInput } from '@blueprintjs/core';
import { toast } from 'sonner';
import { setImportCost, type BatchDetail } from './api';
import { leadImportKeys } from './keys';
import { fmtMoney } from './ui';

/**
 * The cost of the file itself, saved when a field loses focus. When the import
 * takes its cost from purchases made under Purchases, those are shown instead.
 */
export default function ImportCostFields({ batch, sourceChosen, large }: { batch: BatchDetail; sourceChosen: boolean; large?: boolean }) {
  const queryClient = useQueryClient();
  const own = batch.allocations.find((a) => a.own);
  const shared = batch.allocations.filter((a) => !a.own);
  const [amount, setAmount] = useState(own ? String(own.amount) : '');
  const [reference, setReference] = useState(own?.invoice_reference ?? '');
  const value = Number(amount) > 0 ? Number(amount) : null;
  const save = useMutation({
    // Shares the details scope so a new lead source is stored before the cost.
    scope: { id: `li-details-${batch.id}` },
    mutationFn: () => setImportCost(batch.id, value, reference),
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) }),
      queryClient.invalidateQueries({ queryKey: ['lead-import', 'batches'] }),
      queryClient.invalidateQueries({ queryKey: leadImportKeys.purchases }),
    ]),
    onError: (e) => toast.error((e as Error).message),
  });
  const flush = () => {
    if (value !== (own ? Number(own.amount) : null) || (value != null && reference.trim() !== (own?.invoice_reference ?? ''))) save.mutate();
  };

  if (shared.length > 0) {
    const total = shared.reduce((n, a) => n + Number(a.amount), 0);
    const from = shared.length === 1 ? `purchase ${shared[0].invoice_reference ?? `#${shared[0].purchase}`}` : `${shared.length} purchases`;
    return <p className="li-muted">The cost of this file, {fmtMoney(total)}, is a share of {from}. Change it under Purchases.</p>;
  }
  return (
    <div className="li-fieldpair">
      <div className="li-field">
        <label className="li-field__label" htmlFor={`li-cost-${batch.id}`}>Cost of this file <span className="li-muted">optional</span></label>
        <NumericInput id={`li-cost-${batch.id}`} leftIcon="dollar" min={0} large={large} fill buttonPosition="none" disabled={!sourceChosen}
          value={amount} onValueChange={(_, text) => setAmount(text)} onBlur={flush} placeholder="0.00" />
        <span className="li-field__help">{sourceChosen ? 'Shows as cost per lead in Imports and Reports.' : 'Choose the lead source first.'}</span>
      </div>
      <label className="li-field">
        <span className="li-field__label">Invoice or payment reference <span className="li-muted">optional</span></span>
        <InputGroup large={large} value={reference} disabled={value == null} onChange={(e) => setReference(e.target.value)} onBlur={flush} />
      </label>
    </div>
  );
}
